/**
 * Sponsors Service
 *
 * API endpoints for sponsor operations
 */

import { apiClient } from '../client';

import type { ApiResponse } from '../types';
import type { Sponsor, SponsorWithType } from '@medsuite/models';

export interface CreateSponsorDto {
  investment_id: number;
  sponsor_type_id: number;
  sponsor_name: string;
  sponsor_title: string;
  sponsor_email: string;
  is_research_owner?: boolean;
}

export interface UpdateSponsorDto extends Partial<Omit<CreateSponsorDto, 'investment_id'>> {}

/**
 * Sponsors Service
 */
class SponsorsService {
  /**
   * Get all sponsors for an investment
   */
  async getAll(investmentId: number): Promise<ApiResponse<SponsorWithType[]>> {
    return apiClient.get<SponsorWithType[]>(`/investments/${investmentId}/sponsors`);
  }

  /**
   * Get single sponsor by ID
   */
  async getById(investmentId: number, sponsorId: number): Promise<ApiResponse<Sponsor>> {
    return apiClient.get<Sponsor>(`/investments/${investmentId}/sponsors/${sponsorId}`);
  }

  /**
   * Create new sponsor
   */
  async create(
    investmentId: number,
    data: Omit<CreateSponsorDto, 'investment_id'>
  ): Promise<ApiResponse<Sponsor>> {
    return apiClient.post<Sponsor>(`/investments/${investmentId}/sponsors`, {
      ...data,
      investment_id: investmentId,
    });
  }

  /**
   * Update sponsor
   */
  async update(
    investmentId: number,
    sponsorId: number,
    data: UpdateSponsorDto
  ): Promise<ApiResponse<Sponsor>> {
    return apiClient.put<Sponsor>(`/investments/${investmentId}/sponsors/${sponsorId}`, data);
  }

  /**
   * Delete sponsor
   */
  async delete(investmentId: number, sponsorId: number): Promise<ApiResponse<void>> {
    return apiClient.delete<void>(`/investments/${investmentId}/sponsors/${sponsorId}`);
  }
}

export const sponsorsService = new SponsorsService();
